import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import {
  fetchAllowedMovies, addAllowedMovie, updateAllowedMovie, deleteAllowedMovie,
  fetchUnmatchedTitles, fetchTmdbSettings, saveTmdbApiKey, enrichAllMovies, backfillTitleEn,
  fetchAdminHalls, setAdminHallManualSeats,
  fetchTmdbCandidates, selectTmdbMatch, resetMovieTmdb, fetchMovies, resetBlockedSeats,
  resetAllScreenings,
  fetchSyncStatus, triggerCurationPull, fetchMirrorCompare,
  fetchMovieDuplicates, setMovieCanonical, clearMovieCanonical, mergeMoviesByTmdb,
  fetchDecayProbeCandidates, createDecayProbe, listDecayProbes, getDecayProbeDetail, deleteDecayProbe,
  fetchBlockedSeatsStats, fetchBlockedSeatBreakdown, resetHallBlockedSeats,
  decidePendingBlockedSeats, triggerTicketScan, fetchBlockedSeatEvents, manualBlockSeats,
  backfillTmdbCredits, fetchPredictionDataStats, triggerBuzzCapture, fetchRecentBuzz,
  fetchBuzzSources, saveBuzzSources,
} from '../api/client';
import HallSeatMap from '../components/HallSeatMap';
import PendingBlockedApprovals from '../components/PendingBlockedApprovals';
import MovieListsPanel from '../components/MovieListsPanel';
import { COLOR_OK, COLOR_WARN, COLOR_DANGER, ACCENT } from '../theme';
import { useAuth } from '../context/AuthContext';

function BlockedSeatsManagementPanel() {
  const [bsHalls, setBsHalls] = useState([]);
  const [bsSelectedChain, setBsSelectedChain] = useState('');
  const [bsSelectedCinema, setBsSelectedCinema] = useState('');
  const [bsSelectedKey, setBsSelectedKey] = useState('');
  const [bsBreakdown, setBsBreakdown] = useState(null);
  const [bsLoading, setBsLoading] = useState(false);
  const [bsResetting, setBsResetting] = useState(false);
  const [bsDeciding, setBsDeciding] = useState(false);
  const [bsMessage, setBsMessage] = useState(null);
  const [bsScanning, setBsScanning] = useState(false);
  const [bsScanMsg, setBsScanMsg] = useState(null);
  const [bsSelectedSeats, setBsSelectedSeats] = useState({});
  const [bsManualBusy, setBsManualBusy] = useState(false);

  const loadHalls = () =>
    fetchBlockedSeatsStats()
      .then((data) => {
        const halls = data.halls || [];
        setBsHalls(halls);
        if (halls.length > 0) {
          const first = halls[0];
          setBsSelectedChain(first.chain || '');
          setBsSelectedCinema(String(first.cinema_id));
          setBsSelectedKey('');
          setBsBreakdown(null);
        }
      })
      .catch(() => {});

  useEffect(() => { loadHalls(); }, []); // eslint-disable-line

  useEffect(() => {
    setBsScanMsg(null);
    setBsSelectedSeats({});
    if (!bsSelectedKey) { setBsBreakdown(null); return; }
    const hallInfo = bsHalls.find((h) => `${h.cinema_id}::${h.hall}` === bsSelectedKey);
    if (!hallInfo?.has_stats) { setBsBreakdown(null); return; }
    const [cinemaId, hall] = bsSelectedKey.split('::');
    setBsLoading(true);
    setBsBreakdown(null);
    fetchBlockedSeatBreakdown(cinemaId, hall)
      .then((data) => setBsBreakdown(data))
      .catch(() => setBsBreakdown(null))
      .finally(() => setBsLoading(false));
  }, [bsSelectedKey]); // eslint-disable-line

  // Derived lists for cascading selects
  const bsChains = [...new Set(bsHalls.map((h) => h.chain).filter(Boolean))].sort();
  const bsCinemasForChain = bsSelectedChain
    ? [...new Map(
        bsHalls
          .filter((h) => h.chain === bsSelectedChain)
          .map((h) => [String(h.cinema_id), h])
      ).values()]
    : [];
  const bsHallsForCinema = bsSelectedCinema
    ? bsHalls.filter((h) => String(h.cinema_id) === bsSelectedCinema)
    : [];

  const handleReset = async () => {
    if (!bsSelectedKey) return;
    const [cinemaId, hall] = bsSelectedKey.split('::');
    const hallInfo = bsHalls.find((h) => `${h.cinema_id}::${h.hall}` === bsSelectedKey);
    const label = hallInfo ? `${hallInfo.cinema} • ${hallInfo.hall}` : bsSelectedKey;
    if (!window.confirm(`לאפס את נתוני הכיסאות החסומים עבור ${label}?\n\nפעולה זו תמחק את הלמידה ואת האישורים לאולם זה.`)) return;
    setBsResetting(true);
    setBsMessage(null);
    try {
      const res = await resetHallBlockedSeats(cinemaId, hall);
      setBsMessage({ type: 'success', text: `אולם אופס. ${res.updated_screenings || 0} הקרנות עודכנו.` });
      await loadHalls();
    } catch (_e) {
      setBsMessage({ type: 'error', text: 'שגיאה באיפוס האולם' });
    } finally {
      setBsResetting(false);
    }
  };

  const seatData = bsBreakdown?.all_seat_data || [];

  const selectedHall = bsHalls.find((h) => `${h.cinema_id}::${h.hall}` === bsSelectedKey);

  const handleManualBlock = async () => {
    const seatIds = Object.keys(bsSelectedSeats).filter((k) => bsSelectedSeats[k]);
    if (seatIds.length === 0 || !bsSelectedKey) return;
    const [cinemaId, hall] = bsSelectedKey.split('::');
    if (!window.confirm(`לחסום ידנית ${seatIds.length} מושבים באולם ${hall}?\n\nהכרטיסים ינוכו מהספירה בהקרנות שבהן המושבים לא זמינים.`)) return;
    setBsManualBusy(true);
    setBsMessage(null);
    try {
      const res = await manualBlockSeats(cinemaId, hall, seatIds);
      if (res?.error) {
        setBsMessage({ type: 'error', text: `שגיאה: ${res.error}` });
      } else {
        setBsMessage({ type: 'success', text: `נחסמו ${res.added} מושבים (${res.screenings_updated || 0} הקרנות עודכנו רטרואקטיבית)` });
        setBsSelectedSeats({});
        const data = await fetchBlockedSeatBreakdown(cinemaId, hall);
        setBsBreakdown(data);
        const statsData = await fetchBlockedSeatsStats();
        setBsHalls(statsData.halls || []);
      }
    } catch (_e) {
      setBsMessage({ type: 'error', text: 'שגיאה בחסימה ידנית' });
    } finally {
      setBsManualBusy(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>ניהול כיסאות חסומים</div>
      </div>

      {bsHalls.length === 0 ? (
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>אין נתוני אולמות זמינים עדיין.</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>רשת</label>
              <select
                value={bsSelectedChain}
                onChange={(e) => {
                  const chain = e.target.value;
                  setBsSelectedChain(chain);
                  setBsSelectedKey('');
                  setBsBreakdown(null);
                  const first = bsHalls.find((h) => h.chain === chain);
                  if (first) setBsSelectedCinema(String(first.cinema_id));
                }}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--brd)', background: 'var(--surf)', color: 'var(--txt)', fontSize: 13, fontFamily: 'Heebo, sans-serif' }}
              >
                {bsChains.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>סניף</label>
              <select
                value={bsSelectedCinema}
                onChange={(e) => {
                  setBsSelectedCinema(e.target.value);
                  setBsSelectedKey('');
                  setBsBreakdown(null);
                }}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--brd)', background: 'var(--surf)', color: 'var(--txt)', fontSize: 13, fontFamily: 'Heebo, sans-serif' }}
                disabled={bsCinemasForChain.length === 0}
              >
                {bsCinemasForChain.map((h) => (
                  <option key={h.cinema_id} value={String(h.cinema_id)}>
                    {h.cinema}{h.city ? ` — ${h.city}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>אולם</label>
              <select
                value={bsSelectedKey}
                onChange={(e) => setBsSelectedKey(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--brd)', background: 'var(--surf)', color: 'var(--txt)', fontSize: 13, fontFamily: 'Heebo, sans-serif' }}
                disabled={bsHallsForCinema.length === 0}
              >
                <option value="" disabled>בחר אולם</option>
                {bsHallsForCinema.map((h) => (
                  <option key={`${h.cinema_id}::${h.hall}`} value={`${h.cinema_id}::${h.hall}`}>
                    {h.hall}{h.has_stats ? ` • ${h.blocked_count} חסומים • ${h.scan_count} סריקות` : ' • לא נסרק'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {bsMessage && (
            <div style={{
              padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
              background: bsMessage.type === 'success' ? 'var(--ok-bg)' : 'var(--danger-bg)',
              color: bsMessage.type === 'success' ? COLOR_OK : COLOR_DANGER,
            }}>
              {bsMessage.text}
            </div>
          )}

          {bsLoading && <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 12 }}>טוען פירוט אולם...</div>}

          {!bsLoading && !bsBreakdown && bsSelectedKey && (() => {
            const hallInfo = bsHalls.find((h) => `${h.cinema_id}::${h.hall}` === bsSelectedKey);
            if (!hallInfo) return null;
            return (
              <div style={{ padding: '12px 0' }}>
                <div style={{ fontSize: 13, color: 'var(--txt)', marginBottom: 6 }}>
                  <strong>{hallInfo.cinema} • {hallInfo.hall}</strong>
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
                  {hallInfo.has_stats
                    ? `סריקות שהצטברו: ${hallInfo.scan_count} — נדרשות לפחות 5 לזיהוי מועמדים.`
                    : 'האולם טרם נסרק — אין נתוני למידה.'}
                </div>
                {hallInfo.chain_key && (
                  <div>
                    <button
                      onClick={async () => {
                        setBsScanning(true);
                        setBsScanMsg(null);
                        try {
                          await triggerTicketScan(hallInfo.chain_key);
                          setBsScanMsg(`סריקת כיסאות הופעלה עבור ${hallInfo.chain}. הנתונים יתעדכנו בעוד מספר דקות.`);
                        } catch (_e) {
                          setBsScanMsg('שגיאה בהפעלת הסריקה');
                        } finally {
                          setBsScanning(false);
                        }
                      }}
                      disabled={bsScanning}
                      style={{
                        padding: '7px 14px', borderRadius: 8, border: '1px solid var(--brd)',
                        background: 'var(--surf)', color: 'var(--txt)',
                        fontSize: 13, cursor: bsScanning ? 'not-allowed' : 'pointer',
                        fontFamily: 'Heebo, sans-serif',
                      }}
                    >
                      {bsScanning ? 'מפעיל סריקה...' : `סרוק עכשיו (${hallInfo.chain})`}
                    </button>
                    {bsScanMsg && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>{bsScanMsg}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {bsBreakdown && !bsLoading && (
            <>
              {selectedHall && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <span style={{ padding: '3px 10px', borderRadius: 12, background: 'var(--accent-bg)', color: ACCENT, fontSize: 12, fontWeight: 600 }}>
                    {selectedHall.cinema} • {selectedHall.hall}
                  </span>
                  <span style={{ padding: '3px 10px', borderRadius: 12, background: 'var(--ok-bg)', color: COLOR_OK, fontSize: 12, fontWeight: 600 }}>
                    חסומים: {bsBreakdown.blocked_count}
                  </span>
                  <span style={{ padding: '3px 10px', borderRadius: 12, background: 'var(--faint)', color: 'var(--muted)', fontSize: 12 }}>
                    סריקות: {bsBreakdown.scan_count}
                  </span>
                  {bsBreakdown.pending_blocked_count > 0 && (
                    <span style={{ padding: '3px 10px', borderRadius: 12, background: 'var(--warn-bg)', color: COLOR_WARN, fontSize: 12, fontWeight: 600 }}>
                      ממתינים לאישור: {bsBreakdown.pending_blocked_count}
                    </span>
                  )}
                </div>
              )}

              {bsBreakdown.pending_blocked_count > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`לאשר חסימת ${bsBreakdown.pending_blocked_count} כיסאות באולם ${bsBreakdown.hall}?`)) return;
                      setBsDeciding(true);
                      try {
                        const [cinemaId, hall] = bsSelectedKey.split('::');
                        await decidePendingBlockedSeats(cinemaId, hall, 'approve');
                        const data = await fetchBlockedSeatBreakdown(cinemaId, hall);
                        setBsBreakdown(data);
                        const statsData = await fetchBlockedSeatsStats();
                        setBsHalls(statsData.halls || []);
                      } finally { setBsDeciding(false); }
                    }}
                    disabled={bsDeciding}
                    style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: bsDeciding ? 'var(--faint)' : COLOR_OK, color: '#fff', fontWeight: 700, fontSize: 13, cursor: bsDeciding ? 'not-allowed' : 'pointer' }}
                  >
                    {bsDeciding ? '...' : `אשר חסימה (${bsBreakdown.pending_blocked_count})`}
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`לדחות את ההצעה לחסימת כיסאות באולם ${bsBreakdown.hall}?`)) return;
                      setBsDeciding(true);
                      try {
                        const [cinemaId, hall] = bsSelectedKey.split('::');
                        await decidePendingBlockedSeats(cinemaId, hall, 'reject');
                        const data = await fetchBlockedSeatBreakdown(cinemaId, hall);
                        setBsBreakdown(data);
                        const statsData = await fetchBlockedSeatsStats();
                        setBsHalls(statsData.halls || []);
                      } finally { setBsDeciding(false); }
                    }}
                    disabled={bsDeciding}
                    style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: bsDeciding ? 'var(--faint)' : COLOR_DANGER, color: '#fff', fontWeight: 700, fontSize: 13, cursor: bsDeciding ? 'not-allowed' : 'pointer' }}
                  >
                    {bsDeciding ? '...' : 'דחה'}
                  </button>
                </div>
              )}

              {seatData.length > 0 && <HallSeatMap seatData={seatData} />}

              {(bsBreakdown.sample_screenings || []).length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>הקרנות לאימות — בחר והשווה באתר הרשת:</div>
                  <select
                    style={{
                      width: '100%', fontSize: 12, padding: '6px 10px', borderRadius: 8,
                      border: '1px solid var(--brd)', background: 'var(--surf)',
                      color: 'var(--txt)', direction: 'rtl',
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>בחר הקרנה ({bsBreakdown.sample_screenings.length})</option>
                    {bsBreakdown.sample_screenings.map((s, i) => (
                      <option key={i} value={i}>{s.showtime} — {s.movie_title}</option>
                    ))}
                  </select>
                </div>
              )}

              {(bsBreakdown.seats || []).length > 0 && (
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
                    <button
                      onClick={handleManualBlock}
                      disabled={bsManualBusy || Object.values(bsSelectedSeats).filter(Boolean).length === 0}
                      style={{
                        padding: '6px 14px', borderRadius: 8, border: 'none',
                        background: (bsManualBusy || Object.values(bsSelectedSeats).filter(Boolean).length === 0) ? 'var(--faint)' : COLOR_WARN,
                        color: '#fff', fontWeight: 700, fontSize: 12,
                        cursor: bsManualBusy ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {bsManualBusy ? '...' : `🔒 חסום מסומנים (${Object.values(bsSelectedSeats).filter(Boolean).length})`}
                    </button>
                    <button
                      onClick={() => {
                        const next = {};
                        (bsBreakdown.seats || []).forEach((s) => {
                          if (!s.is_blocked && s.confidence >= 0.99) next[s.seat_id] = true;
                        });
                        setBsSelectedSeats(next);
                      }}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--brd)', background: 'transparent', color: 'var(--txt)', fontSize: 12, cursor: 'pointer' }}
                    >
                      סמן את כל ה-100%
                    </button>
                    {Object.values(bsSelectedSeats).filter(Boolean).length > 0 && (
                      <button
                        onClick={() => setBsSelectedSeats({})}
                        style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--muted)', fontSize: 12, cursor: 'pointer' }}
                      >
                        נקה סימון
                      </button>
                    )}
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                      חסימה ידנית מוגנת משחרור אוטומטי למשך שבוע, ואחריו נדרשות 10 הקרנות זמינות רצופות לשחרור
                    </span>
                  </div>
                <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto', border: '1px solid var(--brd)', borderRadius: 8, marginTop: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: 'var(--surf)', borderBottom: '1px solid var(--brd)' }}>
                        {['', 'Seat', 'Hits', 'Scans', 'Confidence', 'Status'].map((h, i) => (
                          <th key={i} style={{ padding: '7px 10px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(bsBreakdown.seats || []).slice(0, 150).map((seat) => (
                        <tr key={seat.seat_id} style={{ borderBottom: '1px solid var(--brd)' }}>
                          <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                            {!seat.is_blocked && (
                              <input
                                type="checkbox"
                                checked={!!bsSelectedSeats[seat.seat_id]}
                                onChange={(e) => setBsSelectedSeats((prev) => ({ ...prev, [seat.seat_id]: e.target.checked }))}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                          </td>
                          <td style={{ padding: '6px 10px', fontSize: 12, fontFamily: 'monospace' }}>{seat.seat_label || seat.seat_id}</td>
                          <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'center' }}>{seat.hits}</td>
                          <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'center' }}>{seat.scans}</td>
                          <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'center', fontWeight: 700, color: seat.confidence >= 0.8 ? COLOR_OK : COLOR_WARN }}>
                            {(seat.confidence * 100).toFixed(1)}%
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                              background: seat.is_blocked ? 'var(--ok-bg)' : 'var(--faint)',
                              color: seat.is_blocked ? COLOR_OK : 'var(--muted)',
                            }}>
                              {seat.is_blocked ? '✓ חסום' : 'candidate'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}

              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--brd)' }}>
                <button
                  onClick={handleReset}
                  disabled={bsResetting}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: bsResetting ? 'var(--faint)' : COLOR_DANGER,
                    color: bsResetting ? 'var(--muted)' : '#fff',
                    fontWeight: 700, fontSize: 13, cursor: bsResetting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {bsResetting ? 'מאפס...' : 'איפוס אולם זה'}
                </button>
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
                  מוחק את נתוני הלמידה לאולם זה ומאפס ספירת כיסאות חסומים בהקרנות.
                </div>
              </div>
            </>
          )}
        </>
      )}
      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--muted)' }}>
        מוצגים כל האולמות עם הקרנות פעילות. חסימה מוצעת לפי רצף הקרנות עוקבות שבהן המושב לא זמין
        (בשקלול סרטים שונים, שעות שונות ותפוסת אולמות סמוכים); שחרור מתבצע אוטומטית לאחר 5 הקרנות רצופות שבהן המושב חזר למכירה.
      </div>
      <BlockedSeatEventsPanel />
    </div>
  );
}

const EVENT_ACTION_LABELS = {
  propose: 'הצעה חדשה',
  approve: 'אושר',
  reject: 'נדחה',
  auto_release: 'שחרור אוטומטי',
  reset: 'איפוס',
  suppressed: 'הצעה סוננה',
};

const EVENT_ACTION_COLORS = {
  propose: COLOR_WARN,
  approve: COLOR_OK,
  reject: COLOR_DANGER,
  auto_release: ACCENT,
  reset: 'var(--muted)',
};

function BlockedSeatEventsPanel() {
  const [events, setEvents] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchBlockedSeatEvents(50)
      .then((res) => setEvents(res?.items || []))
      .catch(() => setEvents([]));
  }, []);

  if (events.length === 0) return null;
  const shown = expanded ? events : events.slice(0, 8);

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--brd)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 8 }}>
        יומן אירועים — כיסאות חסומים
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        {shown.map((ev) => (
          <div key={ev.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, flexWrap: 'wrap' }}>
            <span style={{
              padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
              background: 'var(--faint)', color: EVENT_ACTION_COLORS[ev.action] || 'var(--muted)',
            }}>
              {EVENT_ACTION_LABELS[ev.action] || ev.action}
            </span>
            <span style={{ color: 'var(--txt)' }}>{ev.cinema} • {ev.hall}</span>
            <span style={{ color: 'var(--muted)' }}>{ev.seat_count} מושבים</span>
            {ev.evidence?.streak_len && (
              <span style={{ color: 'var(--muted)' }}>רצף {ev.evidence.streak_len} הקרנות</span>
            )}
            {ev.evidence?.restored_screenings > 0 && (
              <span style={{ color: 'var(--muted)' }}>הוחזרו כרטיסים ב-{ev.evidence.restored_screenings} הקרנות</span>
            )}
            <span style={{ color: 'var(--muted)', fontSize: 11 }}>
              {ev.created_at ? new Date(ev.created_at).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
          </div>
        ))}
      </div>
      {events.length > 8 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{ marginTop: 6, background: 'none', border: 'none', color: ACCENT, fontSize: 12, cursor: 'pointer', padding: 0 }}
        >
          {expanded ? 'הצג פחות ▴' : `הצג הכל (${events.length}) ▾`}
        </button>
      )}
    </div>
  );
}

// ── Instance mirroring ──────────────────────────────────────────────────────
//
// The real instance holds the real ticket counts. A copy runs at home for a few
// days before each deploy, so a fault is caught where losing measurements costs
// nothing. Curation replicates from the server; the two databases stay separate
// and each keeps its own numbers.

function MirrorSyncPanel({ refreshNonce }) {
  const [status, setStatus] = useState(null);
  const [pull, setPull] = useState(null);      // result | 'loading'
  const [cmp, setCmp] = useState(null);        // result | 'loading'

  useEffect(() => {
    fetchSyncStatus().then(setStatus).catch(() => setStatus(null));
  }, [refreshNonce]);

  if (!status) return null;

  // On the real server this is a one-line statement of fact, not a control:
  // there is nothing here to press, and pressing it is what must not happen.
  if (!status.is_mirror) {
    return (
      <div style={{ background: 'var(--surf)', borderRadius: 16, padding: '16px 24px',
                    boxShadow: 'var(--shadow)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>
          מצב מופע: ראשי
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
          זהו המופע האמיתי. הוא מגיש את החלטות האוצרות לעותקי בדיקה ו<b>לעולם
          לא מייבא</b> — ייבוא כאן היה דורס את העבודה האמיתית. עותק בדיקה מוגדר
          עם <code>INSTANCE_ROLE=mirror</code>.
        </div>
      </div>
    );
  }

  const runPull = async () => {
    setPull('loading');
    try { setPull(await triggerCurationPull(false)); }
    catch { setPull({ ok: false, error: 'הסנכרון נכשל' }); }
  };
  const runCompare = async () => {
    setCmp('loading');
    try { setCmp(await fetchMirrorCompare(7)); }
    catch { setCmp({ ok: false, error: 'ההשוואה נכשלה' }); }
  };

  const VERDICT = {
    ok: { color: COLOR_OK, label: 'תואם' },
    warn: { color: COLOR_WARN, label: 'לבדוק' },
    critical: { color: COLOR_DANGER, label: 'חריג' },
    missing: { color: COLOR_DANGER, label: 'חסר' },
  };
  const btn = (primary) => ({
    padding: '9px 18px', borderRadius: 8,
    border: primary ? 'none' : '1px solid var(--brd)',
    background: primary ? ACCENT : 'var(--surf-alt)',
    color: primary ? '#fff' : 'var(--txt)',
    fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
  });
  const th = { padding: '8px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700,
               color: 'var(--faint)', borderBottom: '1px solid var(--brd)',
               whiteSpace: 'nowrap' };
  const td = { padding: '9px 12px', fontSize: 12, color: 'var(--txt)',
               fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{ background: 'var(--surf)', borderRadius: 16, padding: '20px 24px',
                  boxShadow: 'var(--shadow)' }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>
          סנכרון מול השרת · מופע בדיקה
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
          מושך מ־<code>{status.upstream || '—'}</code> כל שעה. עוברים <b>קישורי סרטים,
          מושבים חסומים, סינון, סרטים מאושרים, אירועים, מפות מושבים ואישורי סניפים</b>.
          ספירות כרטיסים, הקרנות ותוויות אימון <b>לא עוברות</b> — שני המסדים נשארים
          נפרדים לגמרי.
        </div>
        {!status.upstream && (
          <div style={{ color: COLOR_DANGER, fontSize: 12, marginTop: 6 }}>
            <code>MIRROR_UPSTREAM_URL</code> לא הוגדר — הסנכרון לא ירוץ.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={runPull} disabled={pull === 'loading'} style={btn(true)}>
          {pull === 'loading' ? 'מסנכרן...' : 'סנכרן עכשיו'}
        </button>
        <button onClick={runCompare} disabled={cmp === 'loading'} style={btn(false)}>
          {cmp === 'loading' ? 'משווה...' : 'השווה מול השרת'}
        </button>
      </div>

      {pull && pull !== 'loading' && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 10,
                      background: 'var(--surf-alt)', fontSize: 12,
                      color: pull.ok ? 'var(--txt)' : COLOR_DANGER }}>
          {pull.ok ? (
            <>
              {/* Counts are changes made on this run, so a clean second run is all zeros. */}
              קישורי סרטים: {pull.movies_linked} · בוטלו: {pull.movies_unlinked} ·
              {' '}סינון: {pull.movies_filtered + pull.movies_unfiltered} ·
              {' '}TMDB: {pull.tmdb_updated} ·
              {' '}אולמות: {pull.halls_applied} · סניפים: {pull.branches_updated} ·
              {' '}אירועים: {pull.events_upserted}
              {pull.chains_flattened > 0 && ` · שרשראות שתוקנו: ${pull.chains_flattened}`}
              {(pull.deferred + pull.links_deferred) > 0 && (
                <div style={{ color: 'var(--muted)', marginTop: 4 }}>
                  {pull.deferred + pull.links_deferred} החלטות נדחו — הן נוגעות בסרטים
                  או סניפים שהעותק הזה עוד לא סרק. הן יוחלו בסנכרון הבא.
                </div>
              )}
              {pull.movies_not_on_mirror > 0 && (
                <div style={{ color: 'var(--muted)', marginTop: 4 }}>
                  {pull.movies_not_on_mirror} סרטים קיימים בשרת ועוד לא נסרקו כאן — זה
                  מצב רגיל; ההחלטות עליהם יגיעו כשהעותק יגלה אותם.
                </div>
              )}
              {pull.movies_ambiguous > 0 && (
                <div style={{ color: COLOR_WARN, marginTop: 4 }}>
                  {pull.movies_ambiguous} סרטים לא הותאמו כי יש כאן כמה שורות באותה
                  כותרת ולא ניתן לדעת איזו מהן:
                  {' '}{(pull.ambiguous_sample || []).slice(0, 5).join(' · ')}
                </div>
              )}
            </>
          ) : (pull.error || 'שגיאה')}
        </div>
      )}

      {cmp && cmp !== 'loading' && (
        <div style={{ marginTop: 14 }}>
          {!cmp.ok ? (
            <div style={{ fontSize: 12, color: COLOR_DANGER }}>{cmp.error}</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
                            flexWrap: 'wrap', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700,
                               color: (VERDICT[cmp.verdict] || VERDICT.ok).color }}>
                  {cmp.verdict === 'ok' ? 'הגרסה המקומית נראית תקינה'
                    : cmp.verdict === 'warn' ? 'יש פערים ששווה לבדוק'
                    : 'יש פער חריג — אל תעלה לשרת לפני בירור'}
                </span>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {cmp.window && `${cmp.window.from} – ${cmp.window.to}`}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                הפער שמעניין הוא <b>כיסוי הסריקות</b>: מתוך ההקרנות שכבר קרו, כמה
                באמת נמדדו. מספרי הקרנות שזזים באחוזים בודדים הם הפרשי תזמון; כיסוי
                שצונח הוא בילד שהפסיק למדוד.
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                  <thead>
                    <tr>
                      {['רשת', 'הקרנות (בית / שרת)', 'כרטיסים (בית / שרת)',
                        'כיסוי סריקות', 'מצב'].map((h) => (
                        <th key={h} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(cmp.chains || []).map((row) => {
                      const v = VERDICT[row.verdict] || VERDICT.ok;
                      return (
                        <tr key={row.chain} style={{ borderBottom: '1px solid var(--faint)' }}>
                          <td style={{ ...td, fontWeight: 600 }}>{row.chain}</td>
                          <td style={td}>
                            {row.screenings ? `${row.screenings.local} / ${row.screenings.remote}` : '—'}
                            {row.screenings && row.screenings.delta_pct !== null && (
                              <span style={{ color: 'var(--muted)', marginInlineStart: 6 }}>
                                ({row.screenings.delta_pct > 0 ? '+' : ''}{row.screenings.delta_pct}%)
                              </span>
                            )}
                          </td>
                          <td style={td}>
                            {row.tickets ? `${row.tickets.local.toLocaleString()} / ${row.tickets.remote.toLocaleString()}` : '—'}
                          </td>
                          <td style={td}>
                            {row.coverage && row.coverage.local !== null
                              ? `${row.coverage.local}% / ${row.coverage.remote}%` : '—'}
                          </td>
                          <td style={{ ...td, color: v.color, fontWeight: 700 }}>
                            {v.label}
                            <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--muted)' }}>
                              {row.note}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {Object.keys(cmp.local_failed_jobs || {}).length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: COLOR_DANGER }}>
                  משימות שנכשלו מקומית: {Object.entries(cmp.local_failed_jobs)
                    .map(([j, n]) => `${j} (${n})`).join(' · ')}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


function AdminPage({ refreshNonce, onNavigate }) {
  const { hasPermission, user } = useAuth();
  const [allowedMovies, setAllowedMovies] = useState([]);
  const [unmatchedMovies, setUnmatchedMovies] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState(null);
  const [showUnmatched, setShowUnmatched] = useState(false);
  // TMDB state
  const [tmdbKey, setTmdbKey] = useState('');
  const [tmdbSettings, setTmdbSettings] = useState(null);
  const [tmdbSaving, setTmdbSaving] = useState(false);
  const [tmdbEnriching, setTmdbEnriching] = useState(false);
  const [halls, setHalls] = useState([]);
  const [hallSeatInputs, setHallSeatInputs] = useState({});
  const [hallSaving, setHallSaving] = useState({});
  const [resettingBlocked, setResettingBlocked] = useState(false);
  const [resettingScreenings, setResettingScreenings] = useState(false);
  const [movieGroups, setMovieGroups] = useState(null);
  const [scanningDupes, setScanningDupes] = useState(false);
  const [mergingTmdb, setMergingTmdb] = useState(false);
  const [groupCanonicalSel, setGroupCanonicalSel] = useState({});

  const loadData = useCallback(() => {
    Promise.all([
      fetchAllowedMovies().catch(() => []),
      fetchUnmatchedTitles().catch(() => []),
      fetchTmdbSettings().catch(() => ({ has_key: false })),
      fetchAdminHalls().catch(() => []),
    ]).then(([allowed, unmatched, tmdb, hallsRes]) => {
      setAllowedMovies(allowed);
      setUnmatchedMovies(unmatched);
      setTmdbSettings(tmdb);
      setHalls(hallsRes);
      setLoading(false);
    });
  }, []);

  const saveHallSeats = async (cinemaId, hall) => {
    const key = `${cinemaId}::${hall}`;
    const raw = hallSeatInputs[key];
    if (raw == null || raw === '') return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    setHallSaving((prev) => ({ ...prev, [key]: true }));
    try {
      await setAdminHallManualSeats(cinemaId, hall, parsed > 0 ? Math.floor(parsed) : null);
      const hallsRes = await fetchAdminHalls();
      setHalls(hallsRes);
    } finally {
      setHallSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData, refreshNonce]);

  const handleAdd = (title) => {
    const t = title || newTitle;
    if (!t.trim()) return;
    setAdding(true);
    setMessage(null);
    addAllowedMovie(t.trim())
      .then((res) => {
        if (res.error) {
          setMessage({ type: 'error', text: res.error + (res.existing ? ` (${res.existing})` : '') });
        } else {
          setMessage({ type: 'success', text: `"${res.title}" נוסף לרשימה` });
          setNewTitle('');
          loadData();
        }
      })
      .catch(() => setMessage({ type: 'error', text: 'שגיאה בהוספת הסרט' }))
      .finally(() => setAdding(false));
  };

  const handleToggle = (id, currentActive) => {
    updateAllowedMovie(id, !currentActive)
      .then(() => loadData())
      .catch(() => setMessage({ type: 'error', text: 'שגיאה בעדכון' }));
  };

  const handleDelete = (id, title) => {
    if (!window.confirm(`למחוק את "${title}" מהרשימה?`)) return;
    deleteAllowedMovie(id)
      .then(() => {
        setMessage({ type: 'success', text: `"${title}" הוסר מהרשימה` });
        loadData();
      })
      .catch(() => setMessage({ type: 'error', text: 'שגיאה במחיקה' }));
  };

  const handleResetBlockedSeats = async () => {
    if (!window.confirm('לאפס את כל הכיסאות החסומים שאושרו, ולנקות למידה קיימת?')) return;
    setResettingBlocked(true);
    setMessage(null);
    try {
      const res = await resetBlockedSeats();
      setMessage({
        type: 'success',
        text: `איפוס הושלם: ${res.deleted_halls || 0} אולמות, ${res.updated_screenings || 0} הקרנות עודכנו.`,
      });
      loadData();
    } catch (_e) {
      setMessage({ type: 'error', text: 'שגיאה באיפוס הכיסאות החסומים' });
    } finally {
      setResettingBlocked(false);
    }
  };

  const handleScanDuplicates = async () => {
    setScanningDupes(true);
    setMessage(null);
    try {
      const res = await fetchMovieDuplicates();
      setMovieGroups(res.groups || []);
      setGroupCanonicalSel({});
    } catch (_e) {
      setMessage({ type: 'error', text: 'שגיאה בסריקת כפילויות' });
    } finally {
      setScanningDupes(false);
    }
  };

  const handleMergeTmdb = async () => {
    setMergingTmdb(true);
    setMessage(null);
    try {
      const res = await mergeMoviesByTmdb();
      setMessage({ type: 'success', text: `אוחדו ${res.merged || 0} סרטים ב-${res.groups || 0} קבוצות TMDB.` });
      setMovieGroups(null);
    } catch (_e) {
      setMessage({ type: 'error', text: 'שגיאה באיחוד לפי TMDB' });
    } finally {
      setMergingTmdb(false);
    }
  };

  const handleSetCanonical = async (movieId, canonicalId) => {
    try {
      await setMovieCanonical(movieId, canonicalId);
      handleScanDuplicates();
    } catch (_e) {
      setMessage({ type: 'error', text: 'שגיאה בקביעת קנוני' });
    }
  };

  const handleClearCanonical = async (movieId) => {
    try {
      await clearMovieCanonical(movieId);
      handleScanDuplicates();
    } catch (_e) {
      setMessage({ type: 'error', text: 'שגיאה בביטול קישור' });
    }
  };

  const handleResetAllScreenings = async () => {
    if (!window.confirm('⚠ פעולה הרסנית!\n\nלמחוק את כל ההקרנות והסנפשוטים מהמערכת?\n(הקטלוג של הסרטים והקולנועים יישמר)')) return;
    if (!window.confirm('אישור שני: באמת למחוק את כל ההקרנות? אין דרך חזרה.')) return;
    setResettingScreenings(true);
    setMessage(null);
    try {
      const res = await resetAllScreenings();
      if (res?.error) {
        setMessage({ type: 'error', text: `שגיאה: ${res.error}` });
      } else {
        setMessage({
          type: 'success',
          text: `נמחקו ${res.deleted_screenings || 0} הקרנות ו-${res.deleted_snapshots || 0} סנפשוטים.`,
        });
      }
      loadData();
    } catch (_e) {
      setMessage({ type: 'error', text: 'שגיאה במחיקת ההקרנות' });
    } finally {
      setResettingScreenings(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: 'var(--faint)' }}>טוען נתונים...</div>;
  }

  const scraperDebugButtons = [
    { page: 'debug-hot',     label: 'Hot Cinema',  he: 'הוט סינמה',  color: COLOR_DANGER },
    { page: 'debug-mvl',     label: 'Movieland',   he: 'מובילנד',    color: COLOR_DANGER },
    { page: 'debug-cc',      label: 'Cinema City', he: 'סינמה סיטי', color: '#7c3aed' },
    { page: 'debug-lev',     label: 'Lev',         he: 'לב',         color: '#92400e' },
    { page: 'debug-planet',  label: 'Planet',      he: 'פלאנט',      color: '#075985' },
    { page: 'debug-rav_hen',          label: 'Rav-Chen',         he: 'רב-חן',          color: '#0f766e' },
    { page: 'debug-cinematheque_tlv', label: 'Cinematheque TLV', he: 'סינמטק תל אביב', color: '#4f46e5' },
    { page: 'debug-cinematheque_jlm',      label: 'Cinematheque JLM',      he: 'סינמטק ירושלים', color: '#7c3aed' },
    { page: 'debug-cinematheque_herzliya', label: 'Cinematheque Herzliya', he: 'סינמטק הרצליה',  color: '#059669' },
    { page: 'debug-cinematheque_holon',    label: 'Cinematheque Holon',    he: 'סינמטק חולון',   color: '#db2777' },
    { page: 'debug-cinematheque_haifa',    label: 'Cinematheque Haifa',    he: 'סינמטק חיפה',    color: '#0e7490' },
    { page: 'debug-cinematheque_rosh_pina', label: 'Cinematheque Rosh Pina', he: 'סינמטק ראש פינה', color: '#ea580c' },
    { page: 'debug-cinematheque_sderot',   label: 'Cinematheque Sderot',   he: 'סינמטק שדרות',   color: '#854d0e' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{
        background: 'var(--surf)',
        borderRadius: 16,
        padding: '20px 24px',
        boxShadow: 'var(--shadow)',
      }}>
        <div style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>ניהול סרטים</div>
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 14 }}>
          הגדר אילו סרטים המערכת עוקבת אחריהם. רק סרטים ברשימה יקבלו נתוני כרטיסים.
          כאשר הרשימה ריקה, כל הסרטים עוברים ללא סינון.
        </div>
      </div>

      {hasPermission('admin.danger_zone') && <MirrorSyncPanel refreshNonce={refreshNonce} />}

      {/* User management */}
      {(hasPermission('admin.users') || hasPermission('admin.roles')) && <div style={{
        background: 'var(--surf)',
        borderRadius: 16,
        padding: '20px 24px',
        boxShadow: 'var(--shadow)',
      }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>ניהול משתמשים</div>
          <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
            הוסף משתמשים, קבע סיסמאות וסוגי הרשאות.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {hasPermission('admin.users') && (
            <button
              onClick={() => onNavigate && onNavigate('users')}
              style={{
                padding: '10px 18px', borderRadius: 8,
                border: `1px solid ${ACCENT}55`, background: `${ACCENT}18`,
                color: ACCENT, fontWeight: 600, fontSize: 14,
                cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
                minWidth: 160, textAlign: 'center',
              }}
            >
              משתמשים
            </button>
          )}
          {hasPermission('admin.roles') && (
            <button
              onClick={() => onNavigate && onNavigate('user-roles')}
              style={{
                padding: '10px 18px', borderRadius: 8,
                border: `1px solid ${ACCENT}55`, background: `${ACCENT}18`,
                color: ACCENT, fontWeight: 600, fontSize: 14,
                cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
                minWidth: 160, textAlign: 'center',
              }}
            >
              סוגי משתמשים
            </button>
          )}
        </div>
      </div>}

      {/* Per-scraper debug launcher */}
      {hasPermission('admin.scraper_debug') && <div style={{
        background: 'var(--surf)',
        borderRadius: 16,
        padding: '20px 24px',
        boxShadow: 'var(--shadow)',
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>דיבאג סקרייפרים</div>
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 12 }}>
          עמוד ייעודי לכל סקרייפר: לוג חי, דיאגנוסטיקה, צילומי מסך מסוננים וההקרנות ב-DB.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {scraperDebugButtons.map((b) => (
            <button
              key={b.page}
              onClick={() => onNavigate && onNavigate(b.page)}
              style={{
                padding: '10px 18px',
                borderRadius: 8,
                border: `1px solid ${b.color}55`,
                background: `${b.color}18`,
                color: b.color,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'Heebo, sans-serif',
                minWidth: 160,
                textAlign: 'center',
              }}
              title={`פתח עמוד דיבאג — ${b.he}`}
            >
              {b.he} · {b.label}
            </button>
          ))}
        </div>
      </div>}

      {/* Add Movie Form */}
      {hasPermission('admin.add_movie') && <div style={{
        background: 'var(--surf)',
        borderRadius: 16,
        padding: '20px 24px',
        boxShadow: 'var(--shadow)',
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>הוספת סרט</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder="שם הסרט (לדוגמא: צעקה 7)"
            style={{
              flex: 1,
              padding: '9px 14px',
              borderRadius: 9,
              border: '1px solid var(--brd)',
              background: 'var(--surf-alt)',
              color: 'var(--txt)',
              fontSize: 14,
              fontFamily: 'Heebo, sans-serif',
              outline: 'none',
            }}
          />
          <button
            onClick={() => handleAdd()}
            disabled={adding || !newTitle.trim()}
            style={{
              ...btnStyle,
              background: (adding || !newTitle.trim()) ? 'var(--faint)' : 'var(--ok)',
              opacity: (adding || !newTitle.trim()) ? 0.7 : 1,
              cursor: (adding || !newTitle.trim()) ? 'not-allowed' : 'pointer',
            }}
          >
            {adding ? 'מוסיף...' : '+ הוסף סרט'}
          </button>
        </div>
      </div>}

      {/* Message */}
      {message && (
        <div style={{
          background: message.type === 'error' ? 'var(--danger-bg)' : 'var(--ok-bg)',
          border: `1px solid ${message.type === 'error' ? 'var(--danger)' : 'var(--ok)'}`,
          borderRadius: 8,
          padding: '12px 16px',
          color: message.type === 'error' ? 'var(--danger)' : 'var(--ok)',
          fontSize: 14,
        }}>
          {message.text}
        </div>
      )}

      {/* Blocked-seats reset */}
      {hasPermission('admin.reset_blocked') && <div style={{
        background: 'var(--surf)',
        borderRadius: 16,
        padding: '20px 24px',
        boxShadow: 'var(--shadow)',
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>איפוס מנגנון כיסאות חסומים</div>
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 12 }}>
          מוחק את כל הכיסאות שאושרו כחסומים ומאפס את הלמידה, כדי להתחיל מחדש מאפס.
        </div>
        <button
          onClick={handleResetBlockedSeats}
          disabled={resettingBlocked}
          style={{
            ...btnStyle,
            background: resettingBlocked ? 'var(--faint)' : 'var(--danger)',
            cursor: resettingBlocked ? 'not-allowed' : 'pointer',
          }}
        >
          {resettingBlocked ? 'מאפס...' : 'אפס את כל הכיסאות החסומים'}
        </button>
      </div>}

      {/* Blocked-seats management per-hall */}
      {hasPermission('admin.reset_blocked') && <div style={{
        background: 'var(--surf)',
        borderRadius: 16,
        padding: '20px 24px',
        boxShadow: 'var(--shadow)',
      }}>
        <PendingBlockedApprovals showEmpty />
        <BlockedSeatsManagementPanel />
      </div>}

      {/* The lists a film's distributor and age rating are chosen from (§12.11) */}
      {user?.is_admin && <MovieListsPanel />}

      {/* Movie merge: canonical / variants */}
      {hasPermission('admin.canonical_movies') && <div style={{
        background: 'var(--surf)', borderRadius: 16, padding: '20px 24px',
        boxShadow: 'var(--shadow)',
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>ניהול סרטים — איחוד גרסאות</div>
        </div>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
          אחד סרטים שמופיעים במספר רשתות בכותרות שונות (כולל גרסאות דיבוב/כתוביות) לסרט קנוני אחד.
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <button onClick={handleScanDuplicates} disabled={scanningDupes} style={{
            ...btnStyle,
            background: scanningDupes ? 'var(--faint)' : 'var(--accent)',
            cursor: scanningDupes ? 'not-allowed' : 'pointer',
          }}>
            {scanningDupes ? 'סורק...' : 'סרוק כפילויות'}
          </button>
          <button onClick={handleMergeTmdb} disabled={mergingTmdb} style={{
            ...btnStyle,
            background: mergingTmdb ? 'var(--faint)' : '#7c3aed',
            cursor: mergingTmdb ? 'not-allowed' : 'pointer',
          }}>
            {mergingTmdb ? 'מאחד...' : 'אחד אוטומטית לפי TMDB'}
          </button>
        </div>
        {movieGroups && movieGroups.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--faint)' }}>לא נמצאו קבוצות כפילות.</div>
        )}
        {movieGroups && movieGroups.length > 0 && (
          <div style={{ maxHeight: 480, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {movieGroups.map((g, idx) => {
              const selectedCanonical = groupCanonicalSel[idx] ?? (g.movies.find(m => !m.canonical_movie_id)?.id ?? g.movies[0].id);
              return (
                <div key={idx} style={{
                  padding: 12, background: 'var(--surf-alt)', borderRadius: 8,
                  border: '1px solid var(--brd)',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                    {g.normalized} <span style={{ color: 'var(--faint)', fontWeight: 400 }}>({g.movies.length} סרטים)</span>
                  </div>
                  <div style={{ fontSize: 12, marginBottom: 8 }}>
                    <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                      {g.movies.map(m => (
                        <li key={m.id} style={{ marginBottom: 2 }}>
                          <strong>#{m.id}</strong> — {m.title}
                          {m.canonical_movie_id && (
                            <span style={{ color: 'var(--faint)', marginInlineStart: 6 }}>
                              (גרסה של #{m.canonical_movie_id})
                              <button
                                onClick={() => handleClearCanonical(m.id)}
                                style={{
                                  marginInlineStart: 6, padding: '1px 8px', fontSize: 11,
                                  borderRadius: 4, border: '1px solid var(--brd)',
                                  background: 'transparent', cursor: 'pointer', color: 'var(--muted)',
                                }}
                              >בטל</button>
                            </span>
                          )}
                          {m.tmdb_id && (
                            <span style={{ color: 'var(--faint)', marginInlineStart: 6 }}>
                              TMDB:{m.tmdb_id}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>קנוני:</span>
                    <select
                      value={selectedCanonical}
                      onChange={e => setGroupCanonicalSel(prev => ({ ...prev, [idx]: Number(e.target.value) }))}
                      style={{ padding: '4px 8px', borderRadius: 6, fontSize: 12 }}
                    >
                      {g.movies.map(m => (
                        <option key={m.id} value={m.id}>#{m.id} — {m.title}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        g.movies
                          .filter(m => m.id !== selectedCanonical)
                          .forEach(m => handleSetCanonical(m.id, selectedCanonical));
                      }}
                      style={{
                        padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 600,
                      }}
                    >
                      אחד
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>}

      {/* Danger zone: full screenings reset */}
      {hasPermission('admin.danger_zone') && <div style={{
        background: 'var(--danger-bg)',
        borderRadius: 16,
        padding: '20px 24px',
        border: '1px solid var(--danger)',
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--danger)' }}>⚠ אזור מסוכן — מחיקת כל ההקרנות</div>
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 12, lineHeight: 1.7 }}>
          מחיקה מלאה של כל נתוני ההקרנות מהמערכת (טבלאות <code>Screening</code> ו-<code>TicketSnapshot</code>).
          <br />
          <b style={{ color: 'var(--ok)' }}>נשמרים:</b> רשתות, סניפים, קטלוג סרטים, הגדרות, מנגנון כיסאות חסומים.
          <br />
          <b style={{ color: 'var(--danger)' }}>נמחקים:</b> כל הקרנה שנסרקה אי-פעם וכל סנפשוט כרטיסים.
          <br />
          השתמש רק כאשר חובה להתחיל את המערכת מאפס. אין דרך חזרה.
        </div>
        <button
          onClick={handleResetAllScreenings}
          disabled={resettingScreenings}
          style={{
            ...btnStyle,
            background: resettingScreenings ? 'var(--faint)' : 'var(--danger)',
            cursor: resettingScreenings ? 'not-allowed' : 'pointer',
          }}
        >
          {resettingScreenings ? 'מוחק...' : '🗑 מחק את כל ההקרנות'}
        </button>
      </div>}

      {/* Allowed Movies List */}
      {hasPermission('admin.approved_movies') && <div style={{
        background: 'var(--surf)',
        borderRadius: 16,
        boxShadow: 'var(--shadow)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--brd)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>
            סרטים מאושרים ({allowedMovies.length})
          </div>
          {allowedMovies.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--warn)' }}>
              הרשימה ריקה — כל הסרטים עוברים ללא סינון
            </span>
          )}
        </div>

        {allowedMovies.length > 0 ? (
          <div>
            {/* Table Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 100px 100px 80px',
              gap: 12,
              padding: '10px 24px',
              background: 'var(--surf-alt)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--faint)',
              textTransform: 'uppercase',
            }}>
              <span>שם הסרט</span>
              <span>שם מנורמל</span>
              <span>הקרנות</span>
              <span>סטטוס</span>
              <span></span>
            </div>
            {/* Table Rows */}
            {allowedMovies.map((m) => (
              <div key={m.id} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 100px 100px 80px',
                gap: 12,
                padding: '12px 24px',
                borderBottom: '1px solid var(--brd)',
                alignItems: 'center',
                opacity: m.is_active ? 1 : 0.5,
              }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--txt)' }}>
                  {m.title}
                </span>
                <span style={{ fontSize: 13, color: 'var(--muted)', direction: 'ltr', textAlign: 'right' }}>
                  {m.title_normalized}
                </span>
                <span style={{ fontSize: 13 }}>
                  {m.has_data ? (
                    <span style={{ color: 'var(--ok)' }}>{m.screening_count} הקרנות</span>
                  ) : (
                    <span style={{ color: 'var(--faint)' }}>אין נתונים</span>
                  )}
                </span>
                <button
                  onClick={() => handleToggle(m.id, m.is_active)}
                  style={{
                    background: m.is_active ? 'var(--ok-bg)' : 'var(--danger-bg)',
                    color: m.is_active ? 'var(--ok)' : 'var(--danger)',
                    border: 'none',
                    borderRadius: 6,
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: 'Heebo, sans-serif',
                    cursor: 'pointer',
                  }}
                >
                  {m.is_active ? 'פעיל' : 'מושבת'}
                </button>
                <button
                  onClick={() => handleDelete(m.id, m.title)}
                  style={{
                    background: 'var(--danger-bg)',
                    color: 'var(--danger)',
                    border: 'none',
                    borderRadius: 6,
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: 'Heebo, sans-serif',
                    cursor: 'pointer',
                  }}
                >
                  מחק
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--faint)' }}>
            לא נוספו סרטים עדיין. הוסף סרט למעלה כדי להתחיל לסנן.
          </div>
        )}
      </div>}

      {/* Hall capacities */}
      {hasPermission('admin.hall_capacities') && <div style={{
        background: 'var(--surf)',
        borderRadius: 16,
        boxShadow: 'var(--shadow)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--brd)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>
            ניהול מושבים לפי אולם ({halls.length})
          </div>
        </div>
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {halls.map((h, idx) => {
            const key = `${h.cinema_id}::${h.hall}`;
            return (
              <div key={`${key}-${idx}`} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px 120px 180px',
                gap: 12,
                padding: '10px 24px',
                borderBottom: '1px solid var(--brd)',
                alignItems: 'center',
              }}>
                <div style={{ fontSize: 13 }}>
                  <strong>{h.cinema}</strong> • {h.hall} • {h.city}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
                  inferred: {h.inferred_total_seats || 0}
                </div>
                <input
                  type="number"
                  min="0"
                  value={hallSeatInputs[key] ?? (h.manual_total_seats ?? '')}
                  onChange={(e) => setHallSeatInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                  placeholder="manual"
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: 6,
                    border: '1px solid var(--brd)',
                    background: 'var(--surf-alt)',
                    color: 'var(--txt)',
                    fontSize: 12,
                    fontFamily: 'Heebo, sans-serif',
                    outline: 'none',
                  }}
                />
                <button
                  onClick={() => saveHallSeats(h.cinema_id, h.hall)}
                  disabled={!!hallSaving[key]}
                  style={{
                    ...btnStyle,
                    background: hallSaving[key] ? 'var(--faint)' : 'var(--accent)',
                    cursor: hallSaving[key] ? 'default' : 'pointer',
                    padding: '6px 10px',
                  }}
                >
                  {hallSaving[key] ? 'שומר...' : 'שמור קיבולת אולם'}
                </button>
              </div>
            );
          })}
          {halls.length === 0 && (
            <div style={{ padding: 20, color: 'var(--faint)', textAlign: 'center' }}>אין אולמות זמינים עדיין.</div>
          )}
        </div>
      </div>}

      {/* TMDB Settings */}
      {hasPermission('admin.tmdb_settings') && <div style={{
        background: 'var(--surf)',
        borderRadius: 16,
        padding: '20px 24px',
        boxShadow: 'var(--shadow)',
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>TMDB - מידע על סרטים</div>
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 16 }}>
          חבר מפתח API של TMDB כדי לשלוף פוסטרים, תקצירים בעברית, ז'אנרים והגבלת גיל.
          <a href="https://www.themoviedb.org/settings/api" target="_blank" rel="noopener noreferrer"
             style={{ color: 'var(--danger)', marginRight: 6 }}>
            קבל מפתח API בחינם
          </a>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <input
            type="text"
            value={tmdbKey}
            onChange={(e) => setTmdbKey(e.target.value)}
            placeholder={tmdbSettings?.has_key ? `מפתח נוכחי: ${tmdbSettings.masked_key}` : 'הכנס TMDB API Key'}
            style={{
              flex: 1, padding: '9px 14px', borderRadius: 9,
              border: '1px solid var(--brd)', background: 'var(--surf-alt)',
              color: 'var(--txt)', fontSize: 14, fontFamily: 'Heebo, sans-serif',
              outline: 'none', direction: 'ltr',
            }}
          />
          <button
            onClick={() => {
              if (!tmdbKey.trim()) return;
              setTmdbSaving(true);
              saveTmdbApiKey(tmdbKey.trim())
                .then(() => {
                  setMessage({ type: 'success', text: 'מפתח TMDB נשמר' });
                  setTmdbKey('');
                  loadData();
                })
                .catch(() => setMessage({ type: 'error', text: 'שגיאה בשמירת המפתח' }))
                .finally(() => setTmdbSaving(false));
            }}
            disabled={tmdbSaving || !tmdbKey.trim()}
            style={{
              ...btnStyle,
              background: (tmdbSaving || !tmdbKey.trim()) ? 'var(--faint)' : 'var(--danger)',
              opacity: (tmdbSaving || !tmdbKey.trim()) ? 0.7 : 1,
              cursor: (tmdbSaving || !tmdbKey.trim()) ? 'not-allowed' : 'pointer',
              fontSize: 13,
              padding: '10px 20px',
            }}
          >
            {tmdbSaving ? 'שומר...' : 'שמור'}
          </button>
        </div>
        {tmdbSettings?.has_key && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setTmdbEnriching(true);
                setMessage(null);
                enrichAllMovies()
                  .then((res) => {
                    if (res.error) {
                      setMessage({ type: 'error', text: res.error });
                    } else {
                      setMessage({
                        type: res.failed ? 'error' : 'success',
                        text: `עודכנו ${res.enriched} סרטים מתוך ${res.total}`
                          + (res.failed ? ` · ${res.failed} נכשלו (ראה לוג)` : ''),
                      });
                    }
                  })
                  .catch(() => setMessage({ type: 'error', text: 'שגיאה בשליפת נתונים מ-TMDB' }))
                  .finally(() => setTmdbEnriching(false));
              }}
              disabled={tmdbEnriching}
              style={{
                ...btnStyle,
                background: tmdbEnriching ? 'var(--faint)' : 'var(--danger)',
                opacity: tmdbEnriching ? 0.7 : 1,
                cursor: tmdbEnriching ? 'not-allowed' : 'pointer',
                fontSize: 13,
                padding: '8px 20px',
              }}
            >
              {tmdbEnriching ? 'שולף נתונים...' : 'שלוף מידע מ-TMDB לכל הסרטים'}
            </button>
            <button
              onClick={() => {
                setTmdbEnriching(true);
                setMessage(null);
                backfillTitleEn()
                  .then((res) => {
                    if (res.error) {
                      setMessage({ type: 'error', text: res.error });
                    } else {
                      setMessage({ type: 'success', text: `עודכן שם אנגלי ל-${res.updated} סרטים מתוך ${res.total}` });
                    }
                  })
                  .catch(() => setMessage({ type: 'error', text: 'שגיאה בשליפת שמות אנגליים' }))
                  .finally(() => setTmdbEnriching(false));
              }}
              disabled={tmdbEnriching}
              style={{
                ...btnStyle,
                background: tmdbEnriching ? 'var(--faint)' : 'var(--accent)',
                opacity: tmdbEnriching ? 0.7 : 1,
                cursor: tmdbEnriching ? 'not-allowed' : 'pointer',
                fontSize: 13,
                padding: '8px 20px',
              }}
            >
              {tmdbEnriching ? 'שולף נתונים...' : 'מלא שמות אנגליים (title_en)'}
            </button>
          </div>
        )}
        <PredictionDataPanel />
      </div>}

      {/* TMDB Movie Matching */}
      {hasPermission('admin.tmdb_matching') && tmdbSettings?.has_key && <TmdbMatchingPanel onMessage={setMessage} />}

      {/* Decay Probe */}
      {hasPermission('admin.decay_probe') && <DecayProbePanel />}

      {/* Unmatched Movies */}
      {hasPermission('admin.add_movie') && unmatchedMovies.length > 0 && (
        <div style={{
          background: 'var(--surf)',
          borderRadius: 16,
          boxShadow: 'var(--shadow)',
          overflow: 'hidden',
        }}>
          <button
            onClick={() => setShowUnmatched(!showUnmatched)}
            style={{
              width: '100%',
              padding: '16px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: showUnmatched ? '1px solid var(--brd)' : 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              color: 'var(--txt)',
              fontFamily: 'Heebo, sans-serif',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>
              סרטים לא מזוהים ({unmatchedMovies.length})
            </div>
            <span style={{ color: 'var(--faint)', fontSize: 20 }}>
              {showUnmatched ? '−' : '+'}
            </span>
          </button>

          {showUnmatched && (
            <div>
              <div style={{ padding: '8px 24px', color: 'var(--muted)', fontSize: 13 }}>
                סרטים שנסרקו מאתרי הקולנוע אבל לא תואמים לרשימה המאושרת.
                לחץ "הוסף" כדי להוסיף אותם.
              </div>
              {unmatchedMovies.map((m) => (
                <div key={m.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 24px',
                  borderBottom: '1px solid var(--brd)',
                }}>
                  <div>
                    <span style={{ fontSize: 14, color: 'var(--txt)' }}>{m.title}</span>
                    {m.screening_count > 0 && (
                      <span style={{ fontSize: 12, color: 'var(--faint)', marginRight: 8 }}>
                        ({m.screening_count} הקרנות)
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleAdd(m.title)}
                    style={{
                      background: 'var(--danger-bg)',
                      color: 'var(--danger)',
                      border: 'none',
                      borderRadius: 6,
                      padding: '4px 16px',
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'Heebo, sans-serif',
                      cursor: 'pointer',
                    }}
                  >
                    הוסף
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TmdbMatchingPanel({ onMessage }) {
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchingId, setSearchingId] = useState(null);
  const [candidates, setCandidates] = useState({});
  const [selectingId, setSelectingId] = useState(null);

  const loadMovies = useCallback(() => {
    fetchMovies().then(data => {
      setMovies(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadMovies(); }, [loadMovies]);

  const handleSearch = (movieId) => {
    setSearchingId(movieId);
    fetchTmdbCandidates(movieId)
      .then((res) => {
        if (res.error) {
          onMessage({ type: 'error', text: res.error });
        } else {
          setCandidates(prev => ({ ...prev, [movieId]: res.candidates }));
        }
      })
      .catch(() => onMessage({ type: 'error', text: 'שגיאה בחיפוש TMDB' }))
      .finally(() => setSearchingId(null));
  };

  const handleSelect = (movieId, tmdbId) => {
    setSelectingId(tmdbId);
    selectTmdbMatch(movieId, tmdbId)
      .then((res) => {
        if (res.error) {
          onMessage({ type: 'error', text: res.error });
        } else {
          onMessage({ type: 'success', text: `סרט שויך ל-TMDB: ${res.title_he}` });
          setCandidates(prev => { const n = { ...prev }; delete n[movieId]; return n; });
          loadMovies();
        }
      })
      .catch(() => onMessage({ type: 'error', text: 'שגיאה בשיוך' }))
      .finally(() => setSelectingId(null));
  };

  const handleReset = (movieId, title) => {
    if (!window.confirm(`לאפס את נתוני TMDB עבור "${title}"?`)) return;
    resetMovieTmdb(movieId)
      .then(() => {
        onMessage({ type: 'success', text: `נתוני TMDB אופסו עבור "${title}"` });
        loadMovies();
      })
      .catch(() => onMessage({ type: 'error', text: 'שגיאה באיפוס' }));
  };

  if (loading) return null;

  // Only show movies that have data (screenings)
  const moviesWithData = movies.filter(m => m.screenings_count > 0);

  return (
    <div style={{
      background: 'var(--surf)', borderRadius: 16,
      boxShadow: 'var(--shadow)', overflow: 'hidden',
    }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--brd)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>
          שיוך TMDB לסרטים ({moviesWithData.length})
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>
          לחץ "חפש" כדי לראות 3 תוצאות מ-TMDB ולבחור את ההתאמה הנכונה. לחץ "איפוס" כדי לנקות את הנתונים ולהתחיל מחדש.
        </div>
      </div>
      <div style={{ maxHeight: 500, overflowY: 'auto' }}>
        {moviesWithData.map(m => (
          <div key={m.id}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 120px 80px 80px',
              gap: 8, padding: '10px 24px', borderBottom: '1px solid var(--brd)',
              alignItems: 'center',
            }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--txt)' }}>
                  {m.title_he || m.title}
                </span>
                {m.title_he && <span style={{ fontSize: 12, color: 'var(--faint)', marginRight: 8 }}>{m.title}</span>}
              </div>
              <span style={{
                fontSize: 12,
                color: m.tmdb_rating ? 'var(--ok)' : 'var(--warn)',
              }}>
                {m.tmdb_rating ? `★ ${m.tmdb_rating.toFixed(1)} — TMDB` : 'לא שויך'}
              </span>
              <button
                onClick={() => handleSearch(m.id)}
                disabled={searchingId === m.id}
                style={{
                  background: 'var(--danger-bg)', color: 'var(--danger)',
                  border: 'none', borderRadius: 6, padding: '4px 10px',
                  fontSize: 11, fontWeight: 600, fontFamily: 'Heebo, sans-serif',
                  cursor: searchingId === m.id ? 'wait' : 'pointer',
                  opacity: searchingId === m.id ? 0.6 : 1,
                }}
              >
                {searchingId === m.id ? '...' : 'חפש'}
              </button>
              <button
                onClick={() => handleReset(m.id, m.title_he || m.title)}
                style={{
                  background: 'var(--danger-bg)', color: 'var(--danger)',
                  border: 'none', borderRadius: 6, padding: '4px 10px',
                  fontSize: 11, fontWeight: 600, fontFamily: 'Heebo, sans-serif',
                  cursor: 'pointer', opacity: m.tmdb_rating ? 1 : 0.4,
                }}
                disabled={!m.tmdb_rating}
              >
                איפוס
              </button>
            </div>
            {/* TMDB Candidates */}
            {candidates[m.id] && (
              <div style={{ padding: '8px 24px 12px', background: 'var(--surf-alt)' }}>
                {candidates[m.id].length === 0 ? (
                  <div style={{ color: 'var(--faint)', fontSize: 13 }}>לא נמצאו תוצאות ב-TMDB</div>
                ) : (
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {candidates[m.id].map((c) => (
                      <div key={c.tmdb_id} style={{
                        background: 'var(--surf)', borderRadius: 8, padding: 10,
                        border: '1px solid var(--brd)', width: 200, cursor: 'pointer',
                        transition: 'border-color 0.2s',
                      }}
                        onClick={() => handleSelect(m.id, c.tmdb_id)}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--danger)'}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--brd)'}
                      >
                        {selectingId === c.tmdb_id && (
                          <div style={{ color: 'var(--danger)', fontSize: 11, marginBottom: 4 }}>משייך...</div>
                        )}
                        <div style={{ display: 'flex', gap: 8 }}>
                          {c.poster_path && (
                            <img src={c.poster_path} alt="" style={{
                              width: 45, height: 67, borderRadius: 4, objectFit: 'cover', flexShrink: 0,
                            }} />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--txt)', lineHeight: 1.3 }}>
                              {c.title}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                              {c.original_title}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>
                              {c.release_date?.slice(0, 4)} · ★ {c.vote_average?.toFixed(1)}
                            </div>
                          </div>
                        </div>
                        {c.overview && (
                          <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 4, lineHeight: 1.4 }}>
                            {c.overview.slice(0, 100)}...
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


const btnStyle = {
  padding: '10px 24px',
  borderRadius: 8,
  border: 'none',
  fontSize: 15,
  fontWeight: 600,
  fontFamily: 'Heebo, sans-serif',
  color: '#fff',
  transition: 'all 0.2s',
};


// ---------------------------------------------------------------------------
// Prediction data foundation panel (v4.4.0)
//
// There is no prediction model — this panel watches the *capture* layer that
// a future model will need. Its main job is the snapshot growth number: the
// presale curve is delta-logged, but every future active screening is
// scanned every 5h with no upper bound on showtime, so volume needs an eye
// on it after deploy.
// ---------------------------------------------------------------------------

function PredictionDataPanel() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const [sources, setSources] = useState(null);
  const [ytKey, setYtKey] = useState('');
  const [showData, setShowData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchPredictionDataStats().then(setStats),
      fetchRecentBuzz(14).then(setRecent).catch(() => setRecent(null)),
      fetchBuzzSources().then(setSources).catch(() => setSources(null)),
    ])
      .catch(() => setError('שגיאה בטעינת נתוני האיסוף'))
      .finally(() => setLoading(false));
  }, []);

  const saveSources = (payload) => {
    setBusy('sources'); setMsg(null);
    saveBuzzSources(payload)
      .then(() => { setYtKey(''); setMsg({ type: 'ok', text: 'הגדרות המקורות נשמרו' }); })
      .catch(() => setMsg({ type: 'err', text: 'שגיאה בשמירת ההגדרות' }))
      .finally(() => { setBusy(''); load(); });
  };

  useEffect(() => { load(); }, [load]);

  // Each reason names one concrete cause. "0 rows" on its own sent the
  // operator to a log line that was never written.
  const BUZZ_REASONS = {
    already_captured: 'התאריך הזה כבר נאסף — אין מה להוסיף',
    no_movies_in_scope: 'אין סרטים בטווח הנאסף (−14 עד +60 יום)',
    no_articles_matched: 'לא נמצאו ערכי ויקיפדיה תואמים לסרטים בטווח',
    no_data_from_wikipedia: 'ויקיפדיה נגישה, אך לא הוחזרו נתוני צפיות',
    upstream_unreachable: 'לא ניתן להגיע לוויקיפדיה מהשרת',
    upstream_throttled: 'ויקיפדיה חוסמת בקצב (429) — הרץ שוב בעוד כמה דקות',
    job_error: 'שגיאה בתהליך האיסוף — ראה לוג',
  };

  const runBuzz = () => {
    setBusy('buzz'); setMsg(null);
    triggerBuzzCapture()
      .then((r) => {
        const st = r.stats || {};
        if (r.rows_written > 0) {
          setMsg({
            type: 'ok',
            text: `נאספו ${r.rows_written} רשומות באזז (עד ${r.target_date})`
              + (st.days_backfilled ? ` · ${st.days_backfilled} ימים הושלמו רטרואקטיבית` : '')
              + (r.remaining ? ` · נותרו ${r.remaining} סרטים — הרץ שוב` : ''),
          });
          return;
        }
        const detail = st.requests
          ? ` (${st.requests} בקשות, ${st.resolved} ערכים, ${st.throttled} נחסמו בקצב${
              st.sample_error ? `, שגיאה לדוגמה: ${st.sample_error}` : ''})`
          : '';
        setMsg({
          type: r.reason === 'already_captured' ? 'ok' : 'warn',
          text: `${r.target_date}: ${BUZZ_REASONS[r.reason] || r.reason}${detail}`,
        });
      })
      .catch(() => setMsg({ type: 'err', text: 'שגיאה בהרצת איסוף הבאזז' }))
      .finally(() => { setBusy(''); load(); });
  };

  const runCredits = () => {
    setBusy('credits'); setMsg(null);
    backfillTmdbCredits(300)
      .then((r) => {
        if (r.error) { setMsg({ type: 'err', text: r.error }); return; }
        setMsg({
          type: 'ok',
          text: `עודכנו ${r.updated} סרטים${r.failed ? `, ${r.failed} נכשלו` : ''}. `
            + (r.remaining > 0 ? `נותרו ${r.remaining} — הרץ שוב.` : 'הושלם.'),
        });
      })
      .catch(() => setMsg({ type: 'err', text: 'שגיאה בהשלמת קרדיטים' }))
      .finally(() => { setBusy(''); load(); });
  };

  const num = (n) => (n ?? 0).toLocaleString('he-IL');
  const Stat = ({ label, value, hint, warn }) => (
    <div style={{
      background: 'var(--card)', borderRadius: 10, padding: '10px 14px', minWidth: 150,
      border: warn ? `1px solid ${COLOR_WARN}` : '1px solid transparent',
    }}>
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: warn ? COLOR_WARN : 'var(--txt)' }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{hint}</div>}
    </div>
  );

  const s = stats?.snapshots;
  const l = stats?.labels;
  const f = stats?.movie_features;
  const b = stats?.buzz;

  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--faint)' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)', marginBottom: 4 }}>
        תשתית נתונים לחיזוי
      </div>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
        שכבת <strong>איסוף בלבד</strong> — אין מודל חיזוי, ולא אמור להיות עד שתצטבר היסטוריה.
        עקומת המכירה המוקדמת והבאזז הם סדרות זמן: יום שלא נאסף אבוד לצמיתות.
      </p>

      {error && <div style={{ fontSize: 12, color: COLOR_DANGER, marginBottom: 10 }}>{error}</div>}
      {loading && !stats && <div style={{ fontSize: 12, color: 'var(--muted)' }}>טוען...</div>}

      {stats && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <Stat label="נקודות מכירה מוקדמת" value={num(s.presale)}
                  hint={s.earliest_presale_point ? `מאז ${String(s.earliest_presale_point).slice(0, 10)}` : 'טרם נאספו'} />
            <Stat label="שורות ב-24 שעות" value={num(s.last_24h)}
                  hint={`קצב שנתי ~${num(s.projected_per_year)}`}
                  warn={s.last_24h > 200000} />
            <Stat label="תוויות קפואות" value={num(l.frozen)}
                  hint={l.awaiting_freeze > 0 ? `${num(l.awaiting_freeze)} ממתינות` : 'הכל מעודכן'} />
            <Stat label="סרטים עם קרדיטים" value={`${num(f.with_credits)} / ${num(f.with_tmdb_id)}`}
                  hint={f.credits_backfill_remaining > 0 ? `${num(f.credits_backfill_remaining)} חסרים` : 'הושלם'}
                  warn={f.credits_backfill_remaining > 0} />
            <Stat label="ימי באזז שנאספו" value={num(b.days_captured)}
                  hint={b.last_captured_date ? `אחרון: ${b.last_captured_date}` : 'טרם נאסף'}
                  warn={b.days_captured === 0} />
            {stats.daily_context && (
              <Stat label="ימי הקשר (מזג אוויר/חגים)"
                    value={num(stats.daily_context.with_weather)}
                    hint={stats.daily_context.last
                      ? `עד ${stats.daily_context.last} · ${num(stats.daily_context.holidays)} חגים`
                      : 'טרם נאסף'}
                    warn={!stats.daily_context.with_weather} />
            )}
          </div>

          {s.last_24h > 200000 && (
            <div style={{ fontSize: 12, color: COLOR_WARN, marginBottom: 12, lineHeight: 1.6 }}>
              נפח ה-snapshots גבוה. לדלל לפי מרחק מההקרנה (צפוף ככל שמתקרבים) — <strong>לא</strong> למחוק היסטוריה.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <button onClick={runBuzz} disabled={!!busy} style={{
              ...btnStyle, fontSize: 13, padding: '8px 18px',
              background: busy ? 'var(--faint)' : ACCENT,
              cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
            }}>
              {busy === 'buzz' ? 'אוסף...' : 'הרץ איסוף באזז עכשיו'}
            </button>
            <button onClick={runCredits} disabled={!!busy} style={{
              ...btnStyle, fontSize: 13, padding: '8px 18px',
              background: busy ? 'var(--faint)' : 'var(--danger)',
              cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
            }}>
              {busy === 'credits' ? 'משלים...' : 'השלם קרדיטים לסרטים קיימים'}
            </button>
            <button onClick={load} disabled={loading} style={{
              ...btnStyle, fontSize: 13, padding: '8px 18px',
              background: 'var(--faint)', color: 'var(--txt)', cursor: 'pointer',
            }}>
              רענן
            </button>
          </div>

          {msg && (
            <div style={{
              fontSize: 12, marginBottom: 10,
              color: msg.type === 'ok' ? COLOR_OK : msg.type === 'warn' ? COLOR_WARN : COLOR_DANGER,
            }}>
              {msg.text}
            </div>
          )}

          {/* Which sources feed the series, and what each is worth. Stated
              here because this is where the operator decides. */}
          {sources && (
            <div style={{
              background: 'var(--card)', borderRadius: 10, padding: '12px 14px',
              marginBottom: 14,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>מקורות באזז</div>
              {[
                ['wikipedia', 'ויקיפדיה — צפיות בערך', 'חינם, ללא מפתח. אות חלש אך יציב, וניתן להשלמה אחורה.'],
                ['tmdb_popularity', 'TMDB — מדד פופולריות', 'משתמש במפתח TMDB הקיים. אות טוב. לא ניתן להשלמה אחורה.'],
                ['youtube_trailer_views', 'יוטיוב — צפיות בטריילר', 'האות החזק ביותר. דורש מפתח API חינמי.'],
                ['google_trends', 'Google Trends — עניין בחיפוש', 'ללא API רשמי, נחסם מרוב שרתי ענן, וערכים יחסיים בלבד.'],
              ].map(([key, label, note]) => {
                const s = sources[key] || {};
                return (
                  <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                    <span style={{ color: s.active ? COLOR_OK : 'var(--muted)', fontSize: 13 }}>
                      {s.active ? '●' : '○'}
                    </span>
                    <div style={{ fontSize: 11, lineHeight: 1.5 }}>
                      <strong>{label}</strong>
                      {s.best_effort && <span style={{ color: COLOR_WARN }}> · מאמץ מיטבי</span>}
                      <div style={{ color: 'var(--muted)' }}>{note}</div>
                    </div>
                  </div>
                );
              })}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
                <input
                  type="password"
                  value={ytKey}
                  onChange={(e) => setYtKey(e.target.value)}
                  placeholder={sources.youtube_trailer_views?.masked_key
                    ? `מפתח יוטיוב נוכחי: ${sources.youtube_trailer_views.masked_key}`
                    : 'הדבק כאן מפתח YouTube Data API'}
                  style={{
                    flex: '1 1 240px', padding: '8px 10px', borderRadius: 8,
                    border: '1px solid var(--faint)', background: 'var(--surf)',
                    color: 'var(--txt)', fontSize: 12, fontFamily: 'Heebo, sans-serif',
                  }}
                />
                <button
                  onClick={() => saveSources({ youtube_api_key: ytKey })}
                  disabled={!!busy || !ytKey.trim()}
                  style={{
                    ...btnStyle, fontSize: 12, padding: '8px 16px',
                    background: (busy || !ytKey.trim()) ? 'var(--faint)' : ACCENT,
                    cursor: (busy || !ytKey.trim()) ? 'not-allowed' : 'pointer',
                  }}
                >
                  שמור מפתח
                </button>
                <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!sources.google_trends?.active}
                    onChange={(e) => saveSources({ trends_enabled: e.target.checked })}
                    disabled={!!busy}
                  />
                  הפעל Google Trends
                </label>
              </div>
            </div>
          )}

          {/* What was actually collected. A row count says nothing about
              whether the matched article is the right film. */}
          {recent && (
            <div style={{ marginBottom: 12 }}>
              <button
                onClick={() => setShowData((v) => !v)}
                style={{
                  ...btnStyle, fontSize: 12, padding: '6px 14px',
                  background: 'transparent', color: ACCENT, cursor: 'pointer',
                  border: `1px solid ${ACCENT}`,
                }}
              >
                {showData ? 'הסתר' : `הצג מה נאסף בפועל (${recent.movies_with_data} סרטים)`}
              </button>

              {showData && (
                recent.movies.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
                    עדיין לא נאסף כלום. לחץ "הרץ איסוף באזז עכשיו".
                  </div>
                ) : (
                  <div style={{ marginTop: 10, maxHeight: 340, overflowY: 'auto' }}>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ color: 'var(--muted)', textAlign: 'right' }}>
                          <th style={{ padding: '4px 6px' }}>סרט</th>
                          <th style={{ padding: '4px 6px' }}>מקור</th>
                          <th style={{ padding: '4px 6px' }}>ערך ויקיפדיה שזוהה</th>
                          <th style={{ padding: '4px 6px' }}>ימים</th>
                          <th style={{ padding: '4px 6px' }}>אחרון</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.movies.flatMap((m) =>
                          Object.entries(m.sources).map(([src, data]) => {
                            const last = data.points[data.points.length - 1];
                            const prev = data.points[data.points.length - 2];
                            const delta = prev ? last.value - prev.value : null;
                            return (
                              <tr key={`${m.movie_id}-${src}`} style={{ borderTop: '1px solid var(--faint)' }}>
                                <td style={{ padding: '4px 6px' }}>{m.title}</td>
                                <td style={{ padding: '4px 6px', color: 'var(--muted)' }}>
                                  {src.startsWith('wikipedia') ? `ויקיפדיה ${src.slice(-2)}` : 'TMDB'}
                                </td>
                                <td style={{ padding: '4px 6px', color: 'var(--muted)' }}>
                                  {data.article || '—'}
                                </td>
                                <td style={{ padding: '4px 6px' }}>{data.points.length}</td>
                                <td style={{ padding: '4px 6px' }}>
                                  <strong>{Math.round(last.value).toLocaleString('he-IL')}</strong>
                                  {delta !== null && delta !== 0 && (
                                    <span style={{
                                      marginRight: 6, fontSize: 10,
                                      color: delta > 0 ? COLOR_OK : COLOR_DANGER,
                                    }}>
                                      {delta > 0 ? '▲' : '▼'} {Math.abs(Math.round(delta)).toLocaleString('he-IL')}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 8, lineHeight: 1.6 }}>
                      עמודת "ערך ויקיפדיה שזוהה" היא הבדיקה החשובה — אם השם שם לא מתאים לסרט,
                      ההתאמה שגויה והמספרים חסרי משמעות.
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {stats.chain_coverage?.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.7 }}>
              <strong>כיסוי רשתות</strong> (למניעת השוואת תקופות עם מספר רשתות שונה):{' '}
              {stats.chain_coverage.map((c) => `${c.chain} מ-${c.active_from}`).join(' · ')}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Decay Probe Panel
// ---------------------------------------------------------------------------

const CHAIN_LABELS = {
  hot_cinema: 'הוט סינמה',
  movieland: 'מובילנד',
  cinema_city: 'סינמה סיטי',
  planet: 'פלאנט',
  rav_hen: 'רב-חן',
  lev: 'לב',
  cinematheque_tlv: 'סינמטק תל אביב',
  cinematheque_jlm:      'סינמטק ירושלים',
  cinematheque_herzliya: 'סינמטק הרצליה',
};

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
}

function fmtOffset(min) {
  if (min == null) return '—';
  const sign = min >= 0 ? '+' : '';
  return `${sign}${min.toFixed(1)} דק׳`;
}

function DecayProbePanel() {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState(null);  // null = not yet loaded
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [probes, setProbes] = useState([]);
  const [selectedProbe, setSelectedProbe] = useState(null);
  const [detail, setDetail] = useState(null);
  const [starting, setStarting] = useState({});
  const [deleting, setDeleting] = useState({});
  const pollRef = useRef(null);

  const loadProbes = useCallback(() => {
    listDecayProbes().then(setProbes).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    loadProbes();
    pollRef.current = setInterval(loadProbes, 30000);
    return () => clearInterval(pollRef.current);
  }, [open, loadProbes]);

  useEffect(() => {
    if (selectedProbe == null) { setDetail(null); return; }
    getDecayProbeDetail(selectedProbe).then(setDetail).catch(() => {});
    const t = setInterval(() => {
      getDecayProbeDetail(selectedProbe).then(setDetail).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [selectedProbe]);

  const handleLoadCandidates = () => {
    setLoadingCandidates(true);
    fetchDecayProbeCandidates()
      .then(data => setCandidates(data))
      .catch(() => setCandidates([]))
      .finally(() => setLoadingCandidates(false));
  };

  const handleStart = (screeningId, chain) => {
    setStarting(p => ({ ...p, [screeningId]: true }));
    createDecayProbe(screeningId)
      .then(() => { loadProbes(); setCandidates([]); })
      .catch(() => {})
      .finally(() => setStarting(p => ({ ...p, [screeningId]: false })));
  };

  const handleDelete = (probeId) => {
    if (!window.confirm('לבטל ולמחוק את הניסוי?')) return;
    setDeleting(p => ({ ...p, [probeId]: true }));
    deleteDecayProbe(probeId)
      .then(() => { loadProbes(); if (selectedProbe === probeId) setSelectedProbe(null); })
      .catch(() => {})
      .finally(() => setDeleting(p => ({ ...p, [probeId]: false })));
  };

  const firstErrorOffset = detail?.samples?.find(s => s.error)?.offset_minutes;

  const chartData = (detail?.samples || []).map(s => ({
    offset: parseFloat(s.offset_minutes.toFixed(1)),
    sold: s.error ? null : s.tickets_sold,
    total: s.error ? null : s.total_seats,
    error: s.error ? 1 : null,
  }));

  const statusColor = (s) => {
    if (s === 'running') return 'var(--ok)';
    if (s === 'done') return 'var(--faint)';
    if (s === 'aborted') return 'var(--danger)';
    return 'var(--warn)';
  };

  return (
    <div style={{ background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '16px 24px',
          background: 'transparent', border: 'none',
          borderBottom: open ? '1px solid var(--brd)' : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)', textAlign: 'right' }}>
            ניסוי דעיכה
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'right' }}>
            בדיקת כמה זמן ה-API של כל רשת זמין אחרי תחילת הקרנה
          </div>
        </div>
        <span style={{ color: 'var(--faint)', fontSize: 18 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Start a probe */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 10 }}>
              הפעל ניסוי חדש
            </div>
            <button
              onClick={handleLoadCandidates}
              disabled={loadingCandidates}
              style={{
                background: 'var(--accent)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '7px 18px', fontSize: 13,
                fontWeight: 600, fontFamily: 'Heebo, sans-serif',
                cursor: loadingCandidates ? 'wait' : 'pointer', opacity: loadingCandidates ? 0.6 : 1,
                marginBottom: 12,
              }}
            >
              {loadingCandidates ? 'טוען...' : 'טען הקרנות קרובות'}
            </button>

            {candidates !== null && candidates.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--faint)', marginTop: 4 }}>
                אין הקרנות ב-4 השעות הקרובות עם booking_url
              </div>
            )}

            {candidates !== null && candidates.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {candidates.map(c => (
                  <div key={c.screening_id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', background: 'var(--surf-alt)',
                    borderRadius: 8, border: '1px solid var(--brd)',
                    gap: 12, flexWrap: 'wrap',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--txt)' }}>
                        {CHAIN_LABELS[c.chain] || c.chain}
                      </span>
                      <span style={{ color: 'var(--faint)', fontSize: 12, marginRight: 6 }}>·</span>
                      <span style={{ fontSize: 13, color: 'var(--muted)' }}>{c.title}</span>
                      <span style={{ color: 'var(--faint)', fontSize: 12, marginRight: 6 }}>·</span>
                      <span style={{ fontSize: 12, color: 'var(--faint)' }}>{c.cinema}</span>
                      <span style={{ color: 'var(--faint)', fontSize: 12, marginRight: 6 }}>·</span>
                      <span style={{ fontSize: 12, color: 'var(--ok)' }}>🕐 {fmtTime(c.showtime)}</span>
                    </div>
                    <button
                      onClick={() => handleStart(c.screening_id, c.chain)}
                      disabled={starting[c.screening_id]}
                      style={{
                        background: 'var(--ok)', color: '#fff',
                        border: 'none', borderRadius: 6, padding: '5px 14px',
                        fontSize: 12, fontWeight: 600, fontFamily: 'Heebo, sans-serif',
                        cursor: starting[c.screening_id] ? 'wait' : 'pointer',
                        opacity: starting[c.screening_id] ? 0.6 : 1,
                        flexShrink: 0,
                      }}
                    >
                      {starting[c.screening_id] ? '...' : 'הפעל ניסוי'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Probe list */}
          {probes.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 10 }}>
                ניסויים ({probes.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {probes.map(p => (
                  <div
                    key={p.id}
                    onClick={() => setSelectedProbe(selectedProbe === p.id ? null : p.id)}
                    style={{
                      padding: '8px 12px', borderRadius: 8, border: '1px solid var(--brd)',
                      background: selectedProbe === p.id ? 'var(--surf-alt)' : 'transparent',
                      cursor: 'pointer', display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--txt)' }}>
                          {CHAIN_LABELS[p.chain] || p.chain}
                        </span>
                        <span style={{ color: 'var(--faint)', fontSize: 12, marginRight: 6 }}>·</span>
                        <span style={{ fontSize: 13, color: 'var(--muted)' }}>{p.title}</span>
                        <span style={{ color: 'var(--faint)', fontSize: 12, marginRight: 6 }}>·</span>
                        <span style={{ fontSize: 12, color: 'var(--faint)' }}>
                          {p.sample_count} דגימות
                        </span>
                      </div>
                      {p.result_note && (
                        <div style={{ fontSize: 11, color: 'var(--ok)', marginTop: 2 }}>
                          {p.result_note}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, color: statusColor(p.status),
                        background: 'var(--surf-alt)', borderRadius: 4, padding: '2px 6px',
                      }}>
                        {p.status === 'running' ? '● בריצה' : p.status === 'done' ? '✓ הסתיים' : p.status === 'pending' ? '⏳ ממתין' : 'בוטל'}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                        disabled={deleting[p.id]}
                        style={{
                          background: 'var(--danger-bg)', color: 'var(--danger)',
                          border: 'none', borderRadius: 5, padding: '3px 10px',
                          fontSize: 11, fontWeight: 600, fontFamily: 'Heebo, sans-serif',
                          cursor: 'pointer',
                        }}
                      >
                        מחק
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detail / Chart */}
          {detail && detail.samples && detail.samples.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 12 }}>
                תוצאות — {CHAIN_LABELS[detail.chain] || detail.chain} · {detail.title}
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--brd)" />
                  <XAxis
                    dataKey="offset"
                    tick={{ fontSize: 11, fill: 'var(--faint)' }}
                    label={{ value: 'דקות מתחילת הקרנה', position: 'insideBottom', dy: 10, fontSize: 11, fill: 'var(--muted)' }}
                    height={36}
                  />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--faint)' }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--surf)', border: 'none', borderRadius: 8, fontSize: 12 }}
                    formatter={(v, name) => [v, name === 'sold' ? 'נמכרו' : 'סה״כ']}
                    labelFormatter={l => `${l >= 0 ? '+' : ''}${l} דק׳`}
                  />
                  {firstErrorOffset != null && (
                    <ReferenceLine x={firstErrorOffset} stroke="var(--danger)" strokeDasharray="4 2"
                      label={{ value: 'שגיאה ראשונה', fill: 'var(--danger)', fontSize: 10, position: 'top' }} />
                  )}
                  <Line type="monotone" dataKey="sold" stroke="var(--accent)" strokeWidth={2} dot={false} connectNulls={false} name="sold" />
                  <Line type="monotone" dataKey="total" stroke="var(--ok)" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls={false} name="total" />
                </LineChart>
              </ResponsiveContainer>

              {/* Raw sample table */}
              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--surf-alt)', borderBottom: '1px solid var(--brd)' }}>
                      {['זמן מהתחלה', 'נמכרו', 'סה״כ', 'שגיאה'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--faint)', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.samples.map((s, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--brd)', background: s.error ? 'var(--danger-bg)' : 'transparent' }}>
                        <td style={{ padding: '5px 10px', color: 'var(--txt)', whiteSpace: 'nowrap' }}>{fmtOffset(s.offset_minutes)}</td>
                        <td style={{ padding: '5px 10px', color: 'var(--txt)' }}>{s.tickets_sold ?? '—'}</td>
                        <td style={{ padding: '5px 10px', color: 'var(--muted)' }}>{s.total_seats ?? '—'}</td>
                        <td style={{ padding: '5px 10px', color: 'var(--danger)', fontSize: 11 }}>{s.error || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default AdminPage;
